/**
 * What a strategy is CALLED, where it is being fired.
 *
 * Two different questions, and they have different answers:
 *
 *   `structureLabel` — the shape with its direction word removed, for the
 *   armed chip and the fire buttons. "Sell Short Straddle" names the
 *   direction twice and contradicts itself on the buy side.
 *
 *   `directedName` — the shape under the direction the trader actually
 *   pressed. Buying a Short Straddle IS a Long Straddle, and a confirmation
 *   headed "Short Straddle" above two BUY legs is the product disagreeing
 *   with itself at the one moment it must not.
 *
 * The dropdown LIST keeps the saved names untouched: there, "Short Straddle"
 * and "Long Straddle" are two entries and the word is what tells them apart.
 */

import type { TradeIntent } from '@apna/shared'

/** "Short Straddle" → "Straddle". */
export function structureLabel(name: string): string {
  return name.replace(/^(short|long|bull|bear|broken wing|call ratio)\s+/i, '')
}

/**
 * The name under an intent: "Short Straddle" bought is "Long Straddle".
 *
 * Only the SHORT/LONG pair is rewritten. Bull and Bear name a VIEW rather
 * than a side — the mirror of a Bull Call Spread is a Bear Call Spread,
 * which is a different entry in the catalogue with different strikes, and
 * renaming it here would claim the trader had picked that one. Those keep
 * their saved name, and the SELLING / BUYING badge beside it carries the
 * direction.
 */
export function directedName(name: string, intent: TradeIntent): string {
  const want = intent === 'SELL' ? 'Short' : 'Long'
  return name.replace(/^(short|long)\b/i, (matched) =>
    // Preserve the original's case pattern: a lower-case stored name stays
    // lower case rather than acquiring a capital from this function.
    matched[0] === matched[0]?.toUpperCase() ? want : want.toLowerCase(),
  )
}
